import { Drawer, Select, Typography } from 'antd';
import { useWatch } from 'antd/lib/form/Form';
import { Dispatch, SetStateAction, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIQuery from '../../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../../api/useInfiniteAPIQuery';
import Form from '../../../components/Form';
import RegistrationEnabledFieldsTags from '../../../components/RegistrationEnabledFieldsTags';
import RegistrationTemplateForm, {
  RegistrationTemplateFormValues,
} from '../../../components/RegistrationTemplateForm/RegistrationTemplateForm';
import usePrefetchPages from '../../../hooks/usePrefetchPages';
import useTypedParams from '../../../hooks/useTypedParams';
import useUpdateJobPostingRegistrationTemplate from '../../../hooks/useUpdateJobPostingRegistrationTemplate';
import { JobPostingId } from '../../../types';
import { ActiveRegistrationTemplateSerializer } from '../../../types/api';

const PERSONALIZED_ID = 'personalized';

interface JobPostingRegistrationTemplateFormValues
  extends Omit<RegistrationTemplateFormValues, 'name' | 'defaultTemplate'> {
  id: number | typeof PERSONALIZED_ID;
}

const registrationTemplateToFormValues: (
  registrationTemplate?: Omit<ActiveRegistrationTemplateSerializer, 'block_edit'>
) => JobPostingRegistrationTemplateFormValues = (registrationTemplate) => ({
  id: registrationTemplate?.template_id || PERSONALIZED_ID,
  linkedinOrResume: registrationTemplate?.linkedin_or_resume || 'NOT_REQUIRED',
  linkedin: registrationTemplate?.linkedin || 'NOT_REQUIRED',
  resume: registrationTemplate?.resume || 'NOT_REQUIRED',
  cpf: registrationTemplate?.cpf || 'NOT_VISIBLE',
  pcd: registrationTemplate?.pcd || 'NOT_VISIBLE',
  cid: registrationTemplate?.cid || 'NOT_VISIBLE',
  pcdReport: registrationTemplate?.pcd_report || 'NOT_VISIBLE',
  birthday: registrationTemplate?.birthday || 'NOT_VISIBLE',
  salary: registrationTemplate?.salary || 'NOT_VISIBLE',
  address: registrationTemplate?.address || 'NOT_VISIBLE',
  relocation: registrationTemplate?.relocation || 'NOT_VISIBLE',
  travel: registrationTemplate?.travel || 'NOT_VISIBLE',
  portfolio: registrationTemplate?.portfolio || 'NOT_VISIBLE',
});

interface JobPostingRegistrationTemplateFormProps {
  setIsDirty: Dispatch<SetStateAction<boolean>>;
}

const JobPostingRegistrationTemplateForm = ({
  setIsDirty,
}: JobPostingRegistrationTemplateFormProps) => {
  const { t } = useTranslation();
  const { id } = useTypedParams({ id: JobPostingId });

  const [form] = Form.useForm();

  const [editFormDrawerVisible, setEditFormDrawerVisible] = useState(false);

  const handleClose = () => {
    setEditFormDrawerVisible(false);
  };

  const config = useAPIQuery('/configs/tenant-configurations/');

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const registrationTemplatesPages = useInfiniteAPIQuery('/registration-templates/');

  const registrationTemplates = usePrefetchPages(
    registrationTemplatesPages,
    (data) => data.results
  );

  const fieldsValues = useWatch<JobPostingRegistrationTemplateFormValues | undefined>([], form);

  const activeRegistrationTemplate = jobPosting.data?.active_registration_template;

  const queryClient = useQueryClient();

  const mutation = useUpdateJobPostingRegistrationTemplate({
    onSuccess: () => {
      queryClient.invalidateQueries(`/job-postings/${id}/`);
    },
  });

  return (
    <Form<JobPostingRegistrationTemplateFormValues>
      layout="vertical"
      form={form}
      name="jobPostingRegistrationTemplate"
      initialValues={registrationTemplateToFormValues(activeRegistrationTemplate ?? undefined)}
      onFinish={(variables) => {
        mutation.mutate({
          ...variables,
          registration_template: variables.id !== PERSONALIZED_ID ? variables.id : null,
          jobPostingId: id,
        });
      }}
      onValuesChange={(changedValues) => {
        setIsDirty(true);

        if (changedValues.id !== undefined && changedValues.id !== PERSONALIZED_ID) {
          const registrationTemplate = registrationTemplates.find(
            ({ id }) => id === changedValues.id
          );

          if (registrationTemplate)
            return form.setFieldsValue(
              registrationTemplateToFormValues({
                ...registrationTemplate,
                template_id: registrationTemplate.id,
                personalized_id: null,
              })
            );
        }

        form.setFieldValue('id', PERSONALIZED_ID);
      }}
      component={false}
    >
      <Drawer
        open={editFormDrawerVisible}
        onClose={handleClose}
        width={550}
        title={t('Editar formulário de inscrição')}
        forceRender
      >
        <RegistrationTemplateForm
          isSankhyaActive={config.data?.enableSankhyaAdmission}
          isVixtingActive={config.data?.enableVixtingAdmission}
          disableDefaultTemplateField
          disableNameField
          onAddField={() => {
            setIsDirty(true);
            form.setFieldValue('id', PERSONALIZED_ID);
          }}
          onRemoveField={() => {
            setIsDirty(true);
            form.setFieldValue('id', PERSONALIZED_ID);
          }}
        />
      </Drawer>
      <Form.Row>
        <Form.Col>
          <Typography.Paragraph>
            <Trans>
              Selecione um modelo de questionário para utilizar neste processo seletivo caso deseje
              modificar o modelo padrão. As mudanças aplicadas ao modelo selecionado pelo botão de
              edição abaixo serão aplicadas a esta vaga e não afetarão o uso do modelo em outros
              processos seletivos.
            </Trans>
          </Typography.Paragraph>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col span={8}>
          <Form.Item label={t('Selecione um modelo')} name="id">
            <Select
              options={[
                {
                  key: PERSONALIZED_ID,
                  value: PERSONALIZED_ID,
                  label:
                    (activeRegistrationTemplate?.block_edit && activeRegistrationTemplate.name) ||
                    t('Personalizado'),
                },
                ...registrationTemplates.map(({ id, name }) => ({
                  key: id,
                  value: id,
                  label: name,
                })),
              ]}
              disabled={activeRegistrationTemplate?.block_edit}
            />
          </Form.Item>
        </Form.Col>
      </Form.Row>
      <Form.Row>
        <Form.Col>
          <Typography.Paragraph strong>
            <Trans>Campos habilitados</Trans>
          </Typography.Paragraph>
        </Form.Col>
        <Form.Col flex={1} />
        <Form.Col>
          <Typography.Link
            onClick={() => setEditFormDrawerVisible(true)}
            disabled={activeRegistrationTemplate?.block_edit}
          >
            <Trans>Editar</Trans>
          </Typography.Link>
        </Form.Col>
      </Form.Row>
      <RegistrationEnabledFieldsTags
        fields={{
          address: fieldsValues?.address || 'NOT_VISIBLE',
          birthday: fieldsValues?.birthday || 'NOT_VISIBLE',
          cid: fieldsValues?.cid || 'NOT_VISIBLE',
          cpf: fieldsValues?.cpf || 'NOT_VISIBLE',
          pcd: fieldsValues?.pcd || 'NOT_VISIBLE',
          pcd_report: fieldsValues?.pcdReport || 'NOT_VISIBLE',
          portfolio: fieldsValues?.portfolio || 'NOT_VISIBLE',
          relocation: fieldsValues?.relocation || 'NOT_VISIBLE',
          salary: fieldsValues?.salary || 'NOT_VISIBLE',
          travel: fieldsValues?.travel || 'NOT_VISIBLE',
        }}
        alwaysRequiredCpf={
          config.data?.enableVixtingAdmission || config.data?.enableSankhyaAdmission
        }
      />
    </Form>
  );
};

export default JobPostingRegistrationTemplateForm;
